import React, { useState, useEffect } from 'react';
import { ShoppingBag, Sparkles, Menu, X, Heart, Ruler, Info, HelpCircle, Star, Palette } from 'lucide-react';
import { useCart } from '../context/CartContext';

interface NavbarProps {
  onOpenSizingModal: () => void;
  onOpenCustomOrderModal: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenSizingModal, onOpenCustomOrderModal }) => {
  const { itemCount, openCart } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Shop $30 Sets', href: '#shop', icon: Sparkles },
    { name: 'Meet Kylee', href: '#about', icon: Heart },
    { name: 'Sizing & Care Guide', href: '#sizing', icon: Ruler, onClick: onOpenSizingModal },
    { name: 'Custom Design', href: '#custom-order', icon: Palette, onClick: onOpenCustomOrderModal },
    { name: 'Reviews', href: '#reviews', icon: Star },
    { name: 'FAQ', href: '#faq', icon: HelpCircle },
  ];

  return (
    <header className="sticky top-0 z-40 w-full transition-all duration-300">
      {/* Top Announcement Bar with Logo Palette Colors */}
      <div className="bg-gradient-to-r from-[#D4AF37] via-[#F472B6] via-[#A855F7] to-[#2DD4BF] text-white text-xs sm:text-sm font-medium py-2 px-4 text-center shadow-sm relative overflow-hidden">
        <div className="flex items-center justify-center gap-2 max-w-7xl mx-auto">
          <Sparkles className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: '6s' }} />
          <span>
            <strong className="font-semibold tracking-wide">Grand Opening Special:</strong> All 100% Hand-Painted Sets are only <strong>$30</strong> + Free US Shipping over $50!
          </span>
          <span className="hidden md:inline bg-white/20 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider backdrop-blur-xs">
            Code: KYLEE10 for 10% Off
          </span>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <nav
        className={`w-full transition-all duration-300 ${
          isScrolled
            ? 'bg-white/90 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.06)] border-b border-[#F3C053]/30 py-2.5'
            : 'bg-[#FCFAF8]/80 backdrop-blur-sm border-b border-[#EADFCF]/60 py-3.5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo & Brand Name */}
          <a href="#" className="flex items-center gap-3 group">
            <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full p-0.5 bg-gradient-to-tr from-[#D4AF37] via-[#F472B6] to-[#67E8F9] shadow-md transition-transform group-hover:scale-105">
              <img
                src="/logo.png"
                alt="Nailed It by Kylee Logo"
                className="w-full h-full object-cover rounded-full bg-white"
                onError={(e) => {
                  // Fallback if logo not found directly
                  (e.target as HTMLImageElement).src = '/Nailed It Logo.png';
                }}
              />
              <div className="absolute -bottom-1 -right-1 bg-[#D4AF37] text-white p-0.5 rounded-full text-[10px] shadow-sm">
                ✨
              </div>
            </div>
            <div className="flex flex-col">
              <span className="font-serif text-xl sm:text-2xl font-bold tracking-tight gold-gradient-text leading-tight group-hover:opacity-90">
                Nailed It
              </span>
              <span className="text-[10px] sm:text-[11px] font-sans font-semibold tracking-[0.2em] text-[#78716C] uppercase">
                Artistic Nails <span className="text-[#D4AF37] font-script normal-case tracking-normal text-sm sm:text-base font-bold">by Kylee</span>
              </span>
            </div>
          </a>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => {
                  if (link.onClick) {
                    e.preventDefault();
                    link.onClick();
                  }
                }}
                className="text-sm font-medium text-[#44403C] hover:text-[#B45309] transition-colors relative py-1 group flex items-center gap-1.5"
              >
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#D4AF37] to-[#F472B6] transition-all duration-300 group-hover:w-full rounded-full" />
              </a>
            ))}
          </div>

          {/* Action Buttons: Custom Order + Cart Drawer Trigger */}
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={onOpenCustomOrderModal}
              className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold text-[#854D0E] bg-gradient-to-r from-[#FEF08A] to-[#FCE7F3] border border-[#FDE047] hover:shadow-md transition-all hover:scale-102"
            >
              <Palette className="w-3.5 h-3.5 text-[#D97706]" />
              <span>Custom Order</span>
            </button>

            {/* Shopping Cart Drawer Trigger */}
            <button
              onClick={openCart}
              aria-label="Open Shopping Cart"
              className="relative p-2.5 rounded-full bg-white hover:bg-[#FFFBEB] border border-[#E7E0D6] hover:border-[#D4AF37] text-[#292524] hover:text-[#B45309] shadow-xs transition-all duration-200 group"
            >
              <ShoppingBag className="w-5 h-5 transition-transform group-hover:scale-110" />
              {itemCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-white text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-md animate-bounce">
                  {itemCount}
                </span>
              )}
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Navigation Menu"
              className="lg:hidden p-2 rounded-lg text-[#57534E] hover:text-[#1C1917] hover:bg-[#F5EFE6]"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white/98 border-t border-[#F3C053]/20 px-4 pt-3 pb-6 space-y-2.5 shadow-xl animate-in slide-in-from-top-4 duration-200">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => {
                    if (link.onClick) {
                      e.preventDefault();
                      link.onClick();
                    }
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-base font-medium text-[#292524] hover:bg-[#FEF3C7]/50 hover:text-[#B45309] transition-colors"
                >
                  <Icon className="w-5 h-5 text-[#D4AF37]" />
                  {link.name}
                </a>
              );
            })}
            <div className="pt-2 border-t border-[#F5EBE0]">
              <button
                onClick={() => {
                  onOpenCustomOrderModal();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-white gold-shimmer-btn shadow-md"
              >
                <Palette className="w-4 h-4" />
                Request Custom Nail Design
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
